import { useCallback, useMemo } from 'react';
import { ColumnDef } from '../../../../components/common/AppTable';
import AppGroupedTable, {
  GroupNode,
} from '../../../../components/common/AppGroupedTable';
import { SVG } from '../../../../constants/assetLists';
import { useChecklistContext } from '../../../../context/checklist/ChecklistContextProvider';
import { useClientsContext } from '../../../../context/clients/ClientsContextProvider';
import { useOperationsContext } from '../../../../context/OperationsContextProvider';
import {
  BranchLocationStatus,
  ChecklistGroupClient,
  ClientLocationStatus,
  IChecklist,
} from '../../checklist';
import { getChecklistLocationStatus } from '../../checklistLocationUtils';
import { getCorrectPath } from '../../../../utils';
import { ICONS } from '../../../users/data';

const getDraftCellTextClass = (row: IChecklist) =>
  row.isDraft ? 'text-deepAqua' : undefined;

// Status priority for sorting: active first, then inactive, then cancelled
const STATUS_SORT_ORDER: Record<string, number> = {
  active: 0,
  inactive: 1,
  cancelled: 2,
};

function toGroupNodes(
  groups: ChecklistGroupClient[],
  sortOwn?: (own: IChecklist[]) => IChecklist[],
): GroupNode<IChecklist>[] {
  const apply = (own: IChecklist[]) => (sortOwn ? sortOwn(own) : own);

  return groups.map(client => ({
    id: client.clientId,
    label: client.clientName,
    own: apply(client.own),
    children: client.branches.map(branch => ({
      id: branch.branchId,
      label: branch.branchName,
      own: apply(branch.own),
      children: branch.subBranches.map(sub => ({
        id: sub.subBranchId,
        label: sub.subBranchName,
        own: apply(sub.own),
      })),
    })),
  }));
}

function ChecklistTable() {
  const {
    groupedChecklists,
    selectedChecklists,
    setSelectedChecklists,
    searchText,
    menuSelectedItem,
  } = useChecklistContext();

  const { selectedLocation } = useClientsContext();
  const { shiftTimeList } = useOperationsContext();

  const getLocationStatus = useCallback(
    (
      checklist: IChecklist,
    ): ClientLocationStatus | BranchLocationStatus | null =>
      getChecklistLocationStatus(checklist, selectedLocation),
    [selectedLocation],
  );

  // Sort checklists by status within each group when archived mode is enabled
  // Order: active → inactive → cancelled
  const groups = useMemo(() => {
    const sortOwn =
      menuSelectedItem === 'archived'
        ? (own: IChecklist[]) =>
            [...own].sort((a, b) => {
              const statusA = getLocationStatus(a) ?? 'active';
              const statusB = getLocationStatus(b) ?? 'active';
              return (
                (STATUS_SORT_ORDER[statusA] ?? 0) -
                (STATUS_SORT_ORDER[statusB] ?? 0)
              );
            })
        : undefined;

    return toGroupNodes(groupedChecklists?.groups || [], sortOwn);
  }, [groupedChecklists, menuSelectedItem, getLocationStatus]);

  const columns = useMemo<ColumnDef<IChecklist>[]>(
    () => [
      {
        title: 'שם צ׳קליסט',
        field: 'title',
        accessor: 'title',
        searchable: true,
        cellClassName: 'w-[150px]',
      },
      {
        title: 'מחלקה',
        field: 'department',
        accessor: 'departments',
        cellClassName: 'w-[200px]',
        render: (row) => (
          <div className="flex flex-wrap items-center gap-2">
            {row.departments === 'all' ? (
              <div className="flex items-center gap-2">
                <img
                  src={SVG.saveAllIcon}
                  alt="כל המחלקות"
                  className="w-[20px] h-[20px] object-contain"
                />
                <span>כל המחלקות</span>
              </div>
            ) : Array.isArray(row.departments) ? (
              row.departments.map((dep, index) => (
                <div key={index} className="flex items-center gap-2">
                  {dep.icon && (
                    <img
                      src={
                        dep.icon.startsWith('icon')
                          ? ICONS.find((icon) => icon.id === dep.icon)?.icon
                          : getCorrectPath(dep.icon, 'image')
                      }
                      alt={dep.name}
                      className="w-[20px] h-[20px] object-contain"
                    />
                  )}
                  <span>{dep.name}</span>
                </div>
              ))
            ) : null}
          </div>
        ),
      },
      {
        title: 'שם',
        field: 'name',
        accessor: (r) =>
          `${r.createdBy?.firstName ?? ''} ${r.createdBy?.lastName ?? ''}`.trim(),
        cellClassName: 'w-[150px]',
      },
      {
        title: 'תיאור',
        field: 'description',
        accessor: 'description',
        searchable: true,
        cellClassName: 'w-[200px]',
      },
      {
        title: 'משוייך ל',
        field: 'shiftTime',
        cellClassName: 'w-[150px]',
        accessor: (r) => {
          const shifts = r.shiftTime || [];
          const totalShifts = (shiftTimeList as any)?.shifts?.length || 0;
          if (totalShifts > 0 && shifts.length === totalShifts) {
            return 'כל המשמרות';
          }
          return (
            shifts
              .map((s: any) => s?.name || '')
              .filter(Boolean)
              .join(', ') || '-'
          );
        },
      },
      {
        title: 'צבע',
        field: 'color',
        accessor: (r) =>
          r.departments === 'all'
            ? 'gray'
            : Array.isArray(r.departments)
              ? r.departments.map((d) => d.color).join(', ')
              : '',
        cellClassName: 'w-[90px]',
        render: (r) => (
          <div className="flex flex-wrap items-center gap-1">
            {r.departments === 'all' ? (
              <div
                className="w-[16px] h-[16px] rounded-sm"
                style={{ backgroundColor: 'gray' }}
              />
            ) : Array.isArray(r.departments) ? (
              r.departments.map((dep, index) =>
                dep.color ? (
                  <div
                    key={index}
                    className="w-[16px] h-[16px] rounded-sm"
                    style={{ backgroundColor: dep.color }}
                  />
                ) : null,
              )
            ) : null}
          </div>
        ),
      },
      {
        title: '# משימות',
        field: 'tasks',
        accessor: (r) => r.list?.length || 0,
        cellClassName: 'w-[110px]',
      },
      {
        title: 'סטאטוס',
        field: 'status',
        accessor: (r) =>
          r.isDraft ? 'טיוטה' : (getLocationStatus(r) ?? 'active'),
        cellClassName: 'w-[110px]',
        render: (r) => {
          if (r.isDraft) {
            return (
              <div className="flex items-center gap-2">
                <img src={SVG.draftBlueIcon} className="w-[16px] h-[16px]" />
                <span>טיוטה</span>
              </div>
            );
          }
          const status = getLocationStatus(r);
          const statusLabels: Record<string, string> = {
            active: 'פעיל',
            inactive: 'לא פעיל',
            cancelled: 'מבוטל',
          };
          return (
            <div className="flex items-center gap-2">
              {status === 'active' ? (
                <div className="w-[8px] h-[8px] rounded-full bg-green" />
              ) : status === 'cancelled' ? (
                <img src={SVG.closeRedIcon} className="w-[10px] h-[10px]" />
              ) : (
                <img src={SVG.closeGrayIcon} className="w-[10px] h-[10px]" />
              )}
              <span>{statusLabels[status ?? 'active'] ?? 'פעיל'}</span>
            </div>
          );
        },
      },

      {
        title: 'מוצג בבריף',
        field: 'showInBrief',
        cellClassName: 'w-[110px]',
        accessor: (r) => r.showInBrief,
        render: (r) => {
          const status = getLocationStatus(r);
          return (
            <div className="flex justify-center items-center w-full">
              {r.showInBrief ? (
                <img src={SVG.aquaTick} className="w-[14px] h-[14px]" />
              ) : (
                <span
                  style={{ color: status === 'active' ? '#038D92' : '#707070' }}
                >
                  —
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: 'חובת אישור',
        field: 'requiresApproval',
        cellClassName: 'w-[110px]',
        accessor: (r) => r.approvalRequired,
        render: (r) => {
          const status = getLocationStatus(r);
          return (
            <div className="flex justify-center items-center w-full">
              {r.approvalRequired ? (
                <img src={SVG.aquaTick} className="w-[14px] h-[14px]" />
              ) : (
                <span
                  style={{ color: status === 'active' ? '#038D92' : '#707070' }}
                >
                  —
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: 'בית עסק',
        field: 'clientIds',
        accessor: (r) => {
          const clientNames = r.clientIds?.map((c) => c.name) || [];
          const branchNames = r.branchIds?.map((b) => b.name) || [];
          const allNames = [...clientNames, ...branchNames].filter(Boolean);
          return allNames.join(', ') || '-';
        },
        cellClassName: 'w-[180px]',
      },
    ],
    [getLocationStatus, shiftTimeList],
  );

  return (
    <AppGroupedTable<IChecklist>
      groups={groups}
      columns={columns}
      rowKey={(r) => r._id}
      selectable
      selectedKeys={selectedChecklists}
      onSelectionChange={setSelectedChecklists}
      rowDisabled={(r) => getLocationStatus(r) !== 'active'}
      cellTextClass={getDraftCellTextClass}
      searchText={searchText}
    />
  );
}

export default ChecklistTable;
